import { memo, useRef } from 'react';
import face1 from '../../../../assets/icons/face1.svg';
import face2 from '../../../../assets/icons/face2.svg';
import face3 from '../../../../assets/icons/face3.svg';
import face4 from '../../../../assets/icons/face4.svg';
import face5 from '../../../../assets/icons/face5.svg';
import face11 from '../../../../assets/icons/face11.svg';
import face22 from '../../../../assets/icons/face22.svg';
import face33 from '../../../../assets/icons/face33.svg';
import face44 from '../../../../assets/icons/face44.svg';
import face55 from '../../../../assets/icons/face55.svg';
import styles from './rate.module.css';
import { Col, Row, Tooltip } from 'antd';
import { Question } from '../QuestionsList';
import Totals from './_Totals';
import { Colors } from '../../../core/Theme';
import { Bar } from '@ant-design/charts';
import { formatNumber, formatPercentage } from '../../../../components/Percent';

const facesDef = [face5, face4, face3, face2, face1];
const alts = [
    'Emoticon furioso',
    'Emoticon inconforme',
    'Emoticon neutro',
    'Emoticon sonriendo',
    'Emoticon carcajada',
];

const faces = [face55, face44, face33, face22, face11];
const colors = ['#ff3b3b', '#ff6262', '#ffd56c', '#7FBD5D', '#24dc7d'];

const rangeColors = [Colors.error, Colors.warning, Colors.success];

const Rate = ({ data, question }: { data: any; question: Question }) => {
    const ref = useRef(null);
    const { max_value } = question;
    const top2box =
        (question.extended_props || {}).average_method === 'top2box';

    const ranges = (question.extended_props || {}).average_ranges;

    const renderCount = (num: number) => {
        return (
            <span title={num.toLocaleString()}>
                {num > 999 ? `${(num / 1000).toFixed(1)}k` : num}
            </span>
        );
    };

    const renderFace = (idx: string, image: string, alt: string) => {
        return (
            <div key={image} className={styles.face}>
                <img src={image} alt={alt} />
                {renderCount(
                    data.aggregated.hasOwnProperty(idx)
                        ? data.aggregated[idx].total
                        : 0,
                )}
            </div>
        );
    };

    let elems = [];
    let idxs = [];

    for (let i = 1; i <= 5; i++) {
        if (max_value === 3 && i % 2 === 0) continue;
        elems.push(renderFace(`${i}`, facesDef[i - 1], alts[i - 1]));
        idxs.push(i);
    }

    let max = max_value === 3 ? 2.0 : 4.0;
    let perc_max = Math.max(
        ...idxs.map((k) =>
            data.aggregated[k] ? data.aggregated[k].percentage : 0,
        ),
    );

    return (
        <div className={styles.container} ref={ref}>
            <Row>
                <Col md={24} lg={14}>
                    <div className={styles.bar}>
                        <div
                            className={styles.barFilled}
                            style={{
                                flex: ((data.average - 1) / max).toFixed(2),
                            }}
                        >
                            <div className={styles.handle}>
                                <div>{formatNumber(data.average, 2)}</div>
                                <div className={styles.handleKnob} />
                            </div>
                        </div>
                        <div
                            className={styles.barEmpty}
                            style={{
                                flex: (1 - (data.average - 1) / max).toFixed(2),
                            }}
                        >
                            &nbsp;
                        </div>
                    </div>
                    <div className={styles.faces}>{elems}</div>
                </Col>
                <Col md={24} lg={10}>
                    <div className={styles.chartContainer}>
                        {idxs
                            .map((k) => ({
                                ...(data.aggregated[k] || { percentage: 0 }),
                                id: k,
                            }))
                            .sort((a, b) => b.id - a.id)
                            .map((k) => (
                                <div key={k.id} className={styles.chart}>
                                    <div
                                        className={styles.chartHead}
                                        style={{
                                            backgroundColor: colors[k.id - 1],
                                            borderColor: colors[k.id - 1],
                                        }}
                                    >
                                        <img
                                            src={faces[k.id - 1]}
                                            alt="emoticon"
                                        />
                                    </div>
                                    <Tooltip
                                        title={formatPercentage(
                                            k.percentage,
                                            2,
                                        )}
                                        getPopupContainer={(
                                            node: HTMLElement,
                                        ) =>
                                            node.parentElement || document.body
                                        }
                                        defaultVisible
                                        placement="right"
                                        trigger={[]}
                                    >
                                        <div
                                            className={styles.chartBar}
                                            style={{
                                                flex:
                                                    (k.percentage * 0.8) /
                                                    perc_max,
                                                backgroundColor:
                                                    colors[k.id - 1],
                                            }}
                                        >
                                            &nbsp;
                                        </div>
                                    </Tooltip>
                                </div>
                            ))}
                    </div>
                </Col>
            </Row>
            <Totals data={data} showAverage={true} />
            <h2>Promedio {top2box ? '(Top Two Box)' : ''}</h2>
            <div className={styles.chartScroll}>
                <Bar
                    data={data.consolidated}
                    yField="text"
                    xField={top2box ? 'average' : 'value'}
                    height={data.consolidated.length * 27}
                    xAxis={{ grid: null, label: null }}
                    yAxis={{
                        grid: null,
                        line: null,
                        label: {
                            autoEllipsis: true,
                            style: {
                                fontSize: 11,
                                fontFamily: '"Montserrat", sans-serif',
                                fill: Colors.black,
                                fontWeight: 'normal',
                            },
                        },
                    }}
                    tooltip={false}
                    appendPadding={[0, 0, 0, -32]}
                    label={{
                        style: {
                            fill: Colors.black,
                            fontFamily: '"Montserrat", sans-serif',
                            fontWeight: 'bold',
                            fontSize: 10,
                            textAlign: 'center',
                        },
                        layout: [{ type: 'adjust-color' }],
                        position: 'middle',
                        offsetX: 0,
                        content: (d) =>
                            top2box
                                ? formatPercentage(d.average, 2)
                                : formatNumber(d.value, 2),
                    }}
                    color={({ text }) => {
                        const datum = data.consolidated.find(
                            (c: any) => c.text === text,
                        );
                        if (!datum || datum.text === 'General')
                            return Colors.charts.blue;
                        if (ranges && top2box)
                            return rangeColors[
                                ranges.findIndex((r: any) => datum.average <= r)
                            ];
                        else return colors[Math.round(datum.value) - 1];
                    }}
                />
            </div>
        </div>
    );
};

export default memo(Rate);
